import { type ChangeEvent, useCallback, useState } from 'react';
import { Button } from 'react-bootstrap';
import { FormattedMessage, useIntl } from 'react-intl';

import type { Client } from '@/api/user/getCurrentUser.ts';
import { useCurrentUserQuery } from '@/api/user/index.ts';
import { ClientsTable } from '@/components/ClientSelector/ClientsTable.tsx';
import { useAppDispatch, useAppSelector } from '@/store/hooks.ts';
import { selectSelectedTabType } from '@/store/tabs/selectors.ts';
import { changeClient, setTabType } from '@/store/tabs/slice.ts';
import { TAB_TYPES, type TabType } from '@/store/tabs/types.ts';

interface TabMenuProps {
  tabId: string;
  closeMenu: () => void;
}

export function TabMenu({ tabId, closeMenu }: TabMenuProps) {
  const dispatch = useAppDispatch();
  const [searchTerm, setSearchTerm] = useState('');
  const { data: currentUser } = useCurrentUserQuery();

  const canImportStopLossXLSXFile = currentUser?.productsAccess.stopLoss;
  const canImportFixingXLSXFile = currentUser?.productsAccess.fixing;
  const cantImportBulkOrder = canImportStopLossXLSXFile || canImportFixingXLSXFile;

  const ALL_TAB_TYPES = cantImportBulkOrder ? TAB_TYPES : TAB_TYPES.filter((tab) => tab !== 'bulkOrder');

  const onSelectClient = (clientId: Client['companyId']) => {
    dispatch(changeClient({ tabId, clientId: clientId }));
    closeMenu();
  };

  const onSearchTermChange = (event: ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value);

  return (
    <div className="card" style={{ width: '48rem' }}>
      <div className="card-header gap-2 input-group">
        <SearchInput value={searchTerm} onChange={onSearchTermChange} />
        {ALL_TAB_TYPES.map((tabType) => (
          <ChangeTabTypeButton key={tabType} tabId={tabId} type={tabType} closeMenu={closeMenu} />
        ))}
      </div>

      <div className="card-body">
        <ClientsTable searchTerm={searchTerm} onSelectClient={onSelectClient} />
      </div>
    </div>
  );
}

//////////////////////////
// SECONDARY COMPONENTS //
//////////////////////////

type ChangeTabTypeButtonProps = { tabId: string; type: TabType; closeMenu: () => void };

function ChangeTabTypeButton({ tabId, type, closeMenu }: ChangeTabTypeButtonProps) {
  const dispatch = useAppDispatch();
  const currentType = useAppSelector(selectSelectedTabType);

  const onClick = useCallback(() => {
    dispatch(setTabType({ tabId, type }));
    closeMenu();
  }, [tabId, type, closeMenu]);

  if (currentType === type) {
    return null;
  }

  return (
    <Button variant="default" className="btn-icon-end" onClick={onClick} data-e2e={`switch-${type}-btn`}>
      <FormattedMessage id={`tab.menu.change-type-button.${type}`} />
      <i className="icon">arrow_forward</i>
    </Button>
  );
}

function SearchInput({ value, onChange }: { value: string; onChange: (e: ChangeEvent<HTMLInputElement>) => void }) {
  const { formatMessage } = useIntl();

  return (
    <input
      className="form-control"
      placeholder={formatMessage({ id: 'tab.menu.filter.placeholder' })}
      value={value}
      onChange={onChange}
      data-e2e="client-search-input"
    />
  );
}
